import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { OtherRentalObject, OtherRentalObjectPostTarget } from './../../model/other-rental-object';
import { Article } from '../../model/article';
import { RentalObjectService } from './../base/rental-object';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Observable, catchError, finalize, map, of } from 'rxjs';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { FormOfLease } from '../../model/const/enums';
import { LocalDate } from '../../../core/model/local-date';

@Injectable()
export class OtherRentalObjectService
  extends RentalObjectService<OtherRentalObject>
  implements IPostTargetLoader<OtherRentalObjectPostTarget>
{
  private readonly _defaultFilterFields: string[] = ['companyIds', 'propertyIds', 'rentalId', 'street'];

  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, OtherRentalObject, progressBar, 'other-rental-objects');
  }

  export(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'OvrigaHyresobjekt.xlsx', true);
  }

  getFilterDefinition(): Observable<{
    company: string;
    companyIds: string[];
    property: string;
    propertyIds: string[];
    selectedFields: string[];
    rentalId: string;
    street: string;
    defaultFields: string[];
    lettingFeatureFilters: { lettingFeatureId: string; referenceType: number | null; matchAnyReferenceType: boolean }[];
    article: string;
    articles: Article[];
    formOfLease: number;
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    propertyCustomPropertiesTextValues: { key: string; value: string }[];
    propertyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    propertyCustomPropertiesNumericValues: { key: string; value: string }[];
    propertyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : null,
          companyIds: res.companyIds || [],
          property: res.property ? res.property : null,
          propertyIds: res.propertyIds || [],
          rentalId: res.rentalId ? res.rentalId : '',
          street: res.street ? res.street : '',
          article: res.article || '',
          articles: Array.isArray(res.articles) ? res.articles.map(article => Object.assign(new Article(), article)) : [],
          formOfLease: res.formOfLease || null,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
          lettingFeatureFilters: Array.isArray(res.lettingFeatureFilters) ? res.lettingFeatureFilters : [],
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesTextValues: Array.isArray(res.propertyCustomPropertiesTextValues)
            ? res.propertyCustomPropertiesTextValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesNumericValues: Array.isArray(res.propertyCustomPropertiesNumericValues)
            ? res.propertyCustomPropertiesNumericValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesDateValues: Array.isArray(res.propertyCustomPropertiesDateValues)
            ? res.propertyCustomPropertiesDateValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesBooleanValues: Array.isArray(res.propertyCustomPropertiesBooleanValues)
            ? res.propertyCustomPropertiesBooleanValues.map(v => ({ key: v.key, value: v.value }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getPostTargetModels(propertyId: string): Observable<OtherRentalObjectPostTarget[]> {
    this.progressBar.start();
    return this.client.get<OtherRentalObjectPostTarget[]>(`${this.endpoint}/post-target/${propertyId}`).pipe(
      map(response => response.map(x => new OtherRentalObjectPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }

  getValidFormsOfLease(companyId: string): Observable<FormOfLease[]> {
    return this.client.get<FormOfLease[]>(`${this.endpoint}/formsOfLease/${companyId}`);
  }
}
